import { clampValue } from '@grace-studio/graceful-next/utils';
import {
  AdjustmentFactors,
  MinMaxConversionTableValues,
  NormalizedRecipeValues,
  NormalizedValue,
  RecipeGraphValues,
  RecipeValues,
  Room,
  RoomOptions,
} from 'type';

const multiplyValues = (values: AdjustmentFactors[]) =>
  values
    .filter((value) => value)
    .reduce(
      (valueA, valueB) => ({
        co2: valueA.co2 * valueB.co2,
        luminaireCost: valueA.luminaireCost * valueB.luminaireCost,
        energyCost: valueA.energyCost * valueB.energyCost,
      }),
      {
        co2: 1,
        luminaireCost: 1,
        energyCost: 1,
      },
    );

// const getNormalizedValue = (
//   value: number,
//   conversionTable: ConversionTableValues
// ): NormalizedValue => {
//   const { max1, max2, max3, max4 } = conversionTable;

//   switch (true) {
//     case value <= max1:
//       return 1;
//     case value <= max2:
//       return 2;
//     case value <= max3:
//       return 3;
//     case value <= max4:
//       return 4;
//     default:
//       return 5;
//   }
// };

export const getNormalizedValueFromMinMax = (
  value: number,
  { min, max }: MinMaxConversionTableValues,
) => Math.max(Math.min(((value - min) / (max - min)) * 5, 5), 0);

const combineValues = (roomOptions: RoomOptions): RecipeValues => {
  if (!roomOptions || !roomOptions.lightingSolution) {
    return {
      controlType: 'manual',
      co2: 1,
      luminaireCost: 1,
      energyCost: 1,
      biological: 1,
      emotional: 1,
      visual: 1,
    };
  }

  let adjustmentFactors: AdjustmentFactors[] = [];
  const { controlType, lightingSolution, adjustments } = roomOptions;

  const { bevValues, cleValues: allCleValues } = lightingSolution;

  const cleValues = allCleValues.find(
    (_cleValues) => _cleValues.controlType === controlType,
  ) || { co2: 1, energyCost: 1, luminaireCost: 1 };

  if (
    controlType !== 'manual' &&
    adjustments &&
    adjustments['presence-level']
  ) {
    const { factors } = adjustments['presence-level'];
    adjustmentFactors = [...adjustmentFactors, factors];
  }

  if (adjustments && adjustments['clo']) {
    const { factors } = adjustments['clo'];
    adjustmentFactors = [...adjustmentFactors, factors];
  }

  const adjustedCleValues = multiplyValues([cleValues, ...adjustmentFactors]);

  return {
    controlType,
    ...bevValues,
    ...adjustedCleValues,
  };
};

const normalizeValues = (
  room: Room,
  recipeValues: RecipeValues,
): NormalizedRecipeValues => {
  if (!room || !recipeValues) {
    return {
      co2: 1,
      luminaireCost: 1,
      energyCost: 1,
      biological: 1,
      emotional: 1,
      visual: 1,
    };
  }

  const { co2, luminaireCost, energyCost, biological, emotional, visual } =
    recipeValues;

  const bevValues = { biological, emotional, visual };

  const {
    co2ConversionTable,
    energyCostConversionTable,
    luminaireCostConversionTable,
  } = room;

  const cleValues = {
    co2: getNormalizedValueFromMinMax(co2, co2ConversionTable),
    energyCost: getNormalizedValueFromMinMax(
      energyCost,
      energyCostConversionTable,
    ),
    luminaireCost: getNormalizedValueFromMinMax(
      luminaireCost,
      luminaireCostConversionTable,
    ),
  };

  return {
    ...bevValues,
    ...cleValues,
  };
};

const PERCENT_OF_FULL = 20;

const convertToRecipeGraphValues = (
  recipeValues: NormalizedRecipeValues,
): RecipeGraphValues => ({
  co2: {
    height: recipeValues.co2 * PERCENT_OF_FULL,
    color: clampValue(Math.ceil(recipeValues.co2), 1, 5) as NormalizedValue,
  },
  luminaireCost: {
    height: recipeValues.luminaireCost * PERCENT_OF_FULL,
    color: clampValue(
      Math.ceil(recipeValues.luminaireCost),
      1,
      5,
    ) as NormalizedValue,
  },
  energyCost: {
    height: recipeValues.energyCost * PERCENT_OF_FULL,
    color: clampValue(
      Math.ceil(recipeValues.energyCost),
      1,
      5,
    ) as NormalizedValue,
  },
  biological: {
    height: recipeValues.biological * PERCENT_OF_FULL,
    color: recipeValues.biological,
  },
  emotional: {
    height: recipeValues.emotional * PERCENT_OF_FULL,
    color: recipeValues.emotional,
  },
  visual: {
    height: recipeValues.visual * PERCENT_OF_FULL,
    color: recipeValues.visual,
  },
});

export const RecipeValueFactory = {
  combineValues,
  normalizeValues,
  convertToRecipeGraphValues,
};
